import { Injectable } from '@angular/core';
import { ColorsUtils } from '@utils/ColorsUtils';

import {
	Chart,
	ArcElement,
	LineElement,
	BarElement,
	PointElement,
	BarController,
	BubbleController,
	LineController,
	DoughnutController,
	TimeScale,
	Legend,
	Title,
	Tooltip,
	SubTitle,
	LinearScale,
	CategoryScale
} from 'chart.js';

@Injectable(
	{
		providedIn: 'root'
	})
export class ChartJSService {
	constructor() { }

	async Init() {
		Chart.register
			(
				LinearScale,
				ArcElement,
				LineElement,
				BarElement,
				PointElement,
				BarController,
				BubbleController,
				LineController,
				DoughnutController,
				TimeScale,
				CategoryScale,
				Legend,
				Title,
				Tooltip,
				SubTitle
			);

		return await Promise.resolve(true);
	}


	/** Palavra (invariante entre locales) que identifica o dataset "Total". */
	private static readonly TOTAL_PREFIX = 'total';

	/**
	 * Faz o parsing do label de um dataset no formato "<Total|Ativo> - <NomeModulo>".
	 * O prefixo (antes do primeiro " - ") define a cor/legenda; o sufixo é o nome do módulo.
	 * Labels sem " - " (formato antigo, ex.: "Total"/"Ativo") continuam a funcionar:
	 * o label inteiro é tratado como prefixo e o módulo fica vazio.
	 */
	private ParseDatasetLabel(label: string): { prefix: string, moduleName: string } {
		const raw = (label || '').trim();
		const idx = raw.indexOf(' - ');

		if (idx === -1) return { prefix: raw, moduleName: '' };

		return { prefix: raw.slice(0, idx).trim(), moduleName: raw.slice(idx + 3).trim() };
	}

	/** Um prefixo é "Total" (azul) se corresponder à palavra Total; qualquer outro é o "Ativo" (amarelo). */
	private IsTotalPrefix(prefix: string): boolean {
		return (prefix || '').trim().toLowerCase() === ChartJSService.TOTAL_PREFIX;
	}

	/** Um grupo (Total ou Ativo) está oculto quando todos os seus datasets estão invisíveis. */
	private IsChartGroupHidden(chart: any, isTotal: boolean): boolean {
		const indexes: number[] = (chart?.data?.datasets || [])
			.map((ds: any, i: number) => (!!ds._isTotal === isTotal ? i : -1))
			.filter((i: number) => i >= 0);

		if (!indexes.length) return false;

		return indexes.every((i) => !chart.isDatasetVisible(i));
	}

	/** Cor da paleta pela posição da série (5 cores + cinza para as restantes). */
	private PaletteColor(index: number): string {
		const palette = [
			'--chart-color-primary',
			'--chart-color-secondary',
			'--chart-color-others-1',
			'--chart-color-others-2',
			'--chart-color-others-3',
		];

		return ColorsUtils.CSSVariableValue(palette[index] ?? '--chart-color-others');
	}

	/**
	 *
	 * @param ctx
	 * @param label
	 * @param dataset
	 * @returns
	 */
	GetBarChart(ctx: any, label: string | any, dataset: { data: any, label: string }[] = []) {

		const primaryColor = ColorsUtils.CSSVariableValue('--chart-color-primary');
		const secondaryColor = ColorsUtils.CSSVariableValue('--chart-color-secondary');

		// A convenção Total/Ativo é OPT-IN: só se aplica quando existe pelo menos uma série
		// com o prefixo "Total". Nos gráficos genéricos (1 série por módulo, sem prefixo) a
		// cor vem da posição na paleta — senão sairiam todas iguais e com uma só legenda.
		const hasTotalConvention = (dataset || [])
			.some((el) => this.IsTotalPrefix(this.ParseDatasetLabel(el?.label || '').prefix));

		const datasets = (dataset || []).map((el, index) => {

			const rawLabel = el?.label || '--';
			const { prefix, moduleName } = this.ParseDatasetLabel(rawLabel);
			const isTotal = this.IsTotalPrefix(prefix);
			const color = hasTotalConvention
				? (isTotal ? primaryColor : secondaryColor)
				: this.PaletteColor(index);

			return {
				data: el?.data || [],
				label: rawLabel,
				backgroundColor: color,
				borderColor: color,
				borderWidth: 1,
				// metadados usados pela legenda/tooltip (ignorados pelo Chart.js no render)
				_prefix: prefix,
				_moduleName: moduleName,
				_isTotal: isTotal,
			};
		});

		// Legenda: com a convenção Total/Ativo colapsa em 2 entradas (independente do nº de
		// módulos) e o clique alterna o grupo inteiro. Sem a convenção fica a legenda padrão
		// do Chart.js: uma entrada por série, com a cor respetiva.
		const legendOptions: any = { position: 'bottom', align: 'start' };

		if (hasTotalConvention) {

			legendOptions.labels = {
				generateLabels: (chart: any) => {
					const ds: any[] = chart?.data?.datasets || [];
					const totalDs = ds.find((d) => d._isTotal);
					const activeDs = ds.find((d) => !d._isTotal);

					const items: any[] = [];

					if (totalDs) {
						items.push({
							text: totalDs._prefix || 'Total',
							fillStyle: primaryColor,
							strokeStyle: primaryColor,
							lineWidth: 1,
							hidden: this.IsChartGroupHidden(chart, true),
							_isTotalGroup: true,
						});
					}

					if (activeDs) {
						items.push({
							text: activeDs._prefix || 'Ativo',
							fillStyle: secondaryColor,
							strokeStyle: secondaryColor,
							lineWidth: 1,
							hidden: this.IsChartGroupHidden(chart, false),
							_isTotalGroup: false,
						});
					}

					return items;
				},
			};

			// Toggle por grupo: alterna a visibilidade de todos os datasets Total (ou Ativo).
			legendOptions.onClick = (_e: any, legendItem: any, legend: any) => {
				const chart = legend.chart;
				const isTotal = !!legendItem._isTotalGroup;
				const willShow = this.IsChartGroupHidden(chart, isTotal);

				chart.data.datasets.forEach((ds: any, i: number) => {
					if (!!ds._isTotal === isTotal)
						chart.setDatasetVisibility(i, willShow);
				});

				chart.update();
			};
		}

		const chartBar = new Chart(ctx,
			{
				type: 'bar',
				data:
				{
					labels: label,
					datasets: datasets as any
				},
				options:
				{
					maintainAspectRatio: false,
					scales:
					{
						y: { beginAtZero: true }
					},
					animation: false,
					plugins:
					{
						legend: legendOptions,
						tooltip:
						{
							callbacks:
							{
								// Ex.: "Módulo A — Total: 12" (o nome do módulo aparece aqui, não na legenda).
								label: (context: any) => {
									const ds: any = context.dataset || {};
									const value = context.parsed?.y ?? context.raw;
									const prefix = ds._prefix || ds.label || '';
									const moduleName = ds._moduleName || '';

									return moduleName
										? `${moduleName} — ${prefix}: ${value}`
										: `${prefix}: ${value}`;
								},
							},
						},
					},
				}
			});

		return chartBar;
	}

	/**
	 * 
	 * @param ctx 
	 * @param label 
	 * @param dataset 
	 * @returns 
	 */
	GeLineChart(ctx: any, label: string | any, dataset: { data: any, label: string }[] = []) {

		dataset = dataset.map((el, index) => {

			// console.log("dataset index_>", index);

			let color = ColorsUtils.CSSVariableValue('--chart-color-primary');

			switch (index) {
				case 0:
					color = ColorsUtils.CSSVariableValue('--chart-color-primary');
					break;
				case 1:
					color = ColorsUtils.CSSVariableValue('--chart-color-secondary');
					break;
				case 2:
					color = ColorsUtils.CSSVariableValue('--chart-color-others-1');
					break;
				case 3:
					color = ColorsUtils.CSSVariableValue('--chart-color-others-2');
					break;
				case 4:
					color = ColorsUtils.CSSVariableValue('--chart-color-others-3');
					break;
				default:
					color = ColorsUtils.CSSVariableValue('--chart-color-others');
					break;
			}
			return {
				data: el?.data || [],
				label: el?.label || '--',
				backgroundColor: color,
				borderColor: color,
				borderWidth: 1
			}
		});

		const chartLine = new Chart(ctx,
			{
				type: 'line',
				data:
				{
					labels: label,
					datasets: dataset
				},
				options:
				{
					maintainAspectRatio: false,
					scales:
					{
						y: { beginAtZero: true }
					},
					animation: false,
					plugins:
					{
						legend:
						{
							position: 'bottom',
							align: 'start',
							onClick: (e) => { console.log(e); }
						}
					},
				}
			});

		return chartLine;
	}

	/**
	 * Doughnut/pie: um único dataset cujo array `data` são os valores das fatias e `label` (do IDashboardData)
	 * são os nomes das fatias. A cor é atribuída por fatia a partir da paleta de charts.
	 * @param ctx
	 * @param label nomes das fatias
	 * @param dataset [{ data:number[], label:string }] (usa-se o primeiro)
	 * @returns
	 */
	GetDoughnutChart(ctx: any, label: string | any, dataset: { data: any, label: string }[] = []) {

		const palette = [
			ColorsUtils.CSSVariableValue('--chart-color-primary'),
			ColorsUtils.CSSVariableValue('--chart-color-secondary'),
			ColorsUtils.CSSVariableValue('--chart-color-others-1'),
			ColorsUtils.CSSVariableValue('--chart-color-others-2'),
			ColorsUtils.CSSVariableValue('--chart-color-others-3'),
			ColorsUtils.CSSVariableValue('--chart-color-others'),
		];

		const datasets = (dataset || []).map((el) => ({
			data: el?.data || [],
			label: el?.label || '--',
			// cor por fatia (repete a paleta se houver mais fatias do que cores)
			backgroundColor: (el?.data || []).map((_v: any, i: number) => palette[i % palette.length]),
			borderColor: '#ffffff',
			borderWidth: 2,
		}));

		const chartDoughnut = new Chart(ctx,
			{
				type: 'doughnut',
				data:
				{
					labels: label,
					datasets: datasets as any
				},
				options:
				{
					maintainAspectRatio: false,
					animation: false,
					plugins:
					{
						legend:
						{
							position: 'bottom',
							align: 'start',
						}
					},
				}
			});

		return chartDoughnut;
	}
}
